/**
 * Vendors — MS29 directory index for /vendors.
 *
 * Sister page to the per-vendor landing pages from MS26. Lists every
 * active vendor partner as a card grid, with a category-filter row
 * at the top. Each card links to /vendors/<slug>.
 *
 * Why this exists:
 *   - MS26 added 22 individual vendor pages but no way to browse them
 *   - B2B prospects often want to "see who's on the platform" before
 *     committing — this is the surface for that
 *   - SEO: indexable directory of partners
 *
 * Structure:
 *   - Top nav (logo + back link)
 *   - Hero: eyebrow + Heidi H1 + lede + vendor count
 *   - Category filter chip row (All / Cake / Hamper / Florist / Spa /
 *     Workshop / Voucher / Experience)
 *   - Card grid: vendor name + suburb + category tag + photo
 *   - Footer
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { ArrowRight, MapPin } from "lucide-react";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";
import { useVendorIndex, type VendorIndexCard } from "@/hooks/queries";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

/* ─── Category filter taxonomy ───────────────────────────────────── */

const CATEGORY_LABELS: Record<string, string> = {
  cake: "Cakes",
  hamper: "Hampers",
  florist: "Florists",
  spa: "Spas",
  workshop: "Workshops",
  experience: "Experiences",
  voucher: "Vouchers",
  meal: "Caterers",
};

const CATEGORY_ORDER = [
  "cake",
  "hamper",
  "florist",
  "spa",
  "workshop",
  "experience",
  "voucher",
  "meal",
];

/* ─── Card ───────────────────────────────────────────────────────── */

function VendorCard({ vendor }: { vendor: VendorIndexCard }) {
  const categoryLabel = CATEGORY_LABELS[vendor.primary_category] ?? vendor.primary_category;
  return (
    <Link
      to={`/vendors/${vendor.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white transition-all hover:-translate-y-0.5"
      style={{
        border: "1px solid rgba(29,52,97,0.08)",
        boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
      }}
    >
      {/* Hero photo */}
      <div
        className="overflow-hidden"
        style={{
          aspectRatio: "5 / 3",
          background: "rgba(45,41,38,0.04)",
        }}
      >
        {vendor.hero_image ? (
          <img
            src={vendor.hero_image}
            alt={`${vendor.vendor_name} hero`}
            width={400}
            height={240}
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            className="h-full w-full flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(247,232,164,0.5) 0%, rgba(196,86,74,0.08) 100%)",
            }}
          >
            <span
              className="text-xs uppercase tracking-wider text-navy/40"
              style={{ fontFamily: monoFamily, letterSpacing: "0.16em" }}
            >
              Photo coming
            </span>
          </div>
        )}
      </div>
      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <div
          style={{
            fontFamily: monoFamily,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "rgba(196,86,74,0.85)",
            marginBottom: 6,
          }}
        >
          {categoryLabel}
        </div>
        <h3
          style={{
            fontFamily: displayFamily,
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "1.2rem",
            lineHeight: 1.18,
            letterSpacing: "-0.005em",
            color: "#1D3461",
            margin: 0,
          }}
        >
          {vendor.vendor_name}
        </h3>
        {vendor.delivery_suburbs && (
          <p
            className="mt-1.5 inline-flex items-start gap-1 text-xs leading-snug text-charcoal/55"
            style={{ fontFamily: sansFamily }}
          >
            <MapPin
              size={11}
              strokeWidth={2}
              className="shrink-0 mt-0.5 text-coral/70"
            />
            <span className="line-clamp-2">{vendor.delivery_suburbs}</span>
          </p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <span
            className="text-xs text-charcoal/55"
            style={{ fontFamily: sansFamily }}
          >
            {vendor.product_count} {vendor.product_count === 1 ? "product" : "products"}
          </span>
          <span
            className="inline-flex items-center gap-1 text-xs font-medium text-navy/60 group-hover:text-coral transition-colors"
            style={{ fontFamily: sansFamily }}
          >
            View
            <ArrowRight size={12} strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ─── Filter chip ────────────────────────────────────────────────── */

function FilterChip({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-coral ${
        active
          ? "border-navy bg-navy text-white"
          : "border-navy/15 bg-white text-navy hover:border-coral hover:text-coral"
      }`}
      style={{ fontFamily: sansFamily }}
    >
      {label}
      <span
        className={`text-[11px] ${active ? "text-white/70" : "text-navy/45"}`}
        style={{ fontFamily: monoFamily }}
      >
        {count}
      </span>
    </button>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function Vendors() {
  const vendorsQuery = useVendorIndex();
  const [activeCat, setActiveCat] = useState<string | null>(null);

  // MS139: same useMemo perf pattern as MS136-138. Without this, the
  // `?? []` fallback created a fresh array each render → the `counts`
  // + `filtered` useMemo hooks below re-ran on every render. Public
  // /vendors page is a marketing-funnel surface; cheap fix.
  const all = useMemo(() => vendorsQuery.data ?? [], [vendorsQuery.data]);

  // Build category counts (reactive to data, not state)
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const v of all) {
      c[v.primary_category] = (c[v.primary_category] ?? 0) + 1;
    }
    return c;
  }, [all]);

  const filtered = useMemo(() => {
    if (!activeCat) return all;
    return all.filter((v) => v.primary_category === activeCat);
  }, [all, activeCat]);

  const categoriesPresent = useMemo(
    () => CATEGORY_ORDER.filter((c) => (counts[c] ?? 0) > 0),
    [counts],
  );

  // MS182: structured data for the directory page.
  //   1. BreadcrumbList — 2-rung "Lembra › Vendors" so the
  //      breadcrumb display in SERPs matches the trail MS181 added
  //      on each individual vendor page.
  //   2. CollectionPage wrapping an ItemList of every vendor — gives
  //      Google a single place to discover all 22 vendor entities,
  //      each linked to its canonical /vendors/<slug> URL.
  // Both blocks only emit once the query has data so the prerender
  // doesn't write `numberOfItems: 0` on the initial paint snapshot.
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Lembra", item: "https://lembra.com.au" },
      { "@type": "ListItem", position: 2, name: "Vendors", item: "https://lembra.com.au/vendors" },
    ],
  };

  const collectionLd: Record<string, unknown> | null =
    all.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          url: "https://lembra.com.au/vendors",
          name: "Lembra vendor directory",
          description:
            "Every active vendor partner on Lembra, grouped by category. Cakes, hampers, flowers, spa days, workshops, vouchers, and more.",
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: all.length,
            itemListElement: all.map((v, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              url: `https://lembra.com.au/vendors/${v.slug}`,
              name: v.vendor_name,
            })),
          },
        }
      : null;

  return (
    <>
      <PageMeta
        title="Vendors on Lembra. Melbourne's best for workplace celebrations."
        description="Every vendor partner on Lembra. Cakes, hampers, flowers, spa days, workshops, and vouchers, all from local Melbourne makers, all coordinated by our concierge."
        path="/vendors"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {collectionLd && (
          <script type="application/ld+json">{JSON.stringify(collectionLd)}</script>
        )}
      </Helmet>
      <SkipToContent />

      <div style={{ background: "#FAF6F0", minHeight: "100vh" }}>
        {/* ── Top nav ── */}
        <header className="px-6 md:px-8 pt-6 pb-2">
          <div
            className="flex items-center justify-between"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <Logo size="sm" />
            <Link
              to="/"
              className="text-sm font-medium text-navy/70 hover:text-coral"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to lembra.com.au
            </Link>
          </div>
        </header>

        {/* ── Hero ── */}
        <section
          id="main"
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(48px, 6vw, 72px)",
            paddingBottom: "clamp(32px, 4vw, 56px)",
          }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Lembra · Vendor directory
            </div>
            <h1
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.022em",
                color: "#1D3461",
                margin: 0,
                maxWidth: "20ch",
              }}
            >
              The vendors{" "}
              <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                we work with
              </em>
              .
            </h1>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                marginTop: 18,
                marginBottom: 0,
                maxWidth: "62ch",
              }}
            >
              {vendorsQuery.isLoading
                ? "Loading…"
                : `${all.length} Melbourne vendors across cakes, hampers, flowers, spas, workshops, vouchers, and experiences. Each one vetted, each with their own page on Lembra.`}
            </p>
          </div>
        </section>

        {/* ── Filter chip row ── */}
        {!vendorsQuery.isLoading && all.length > 0 && (
          <section
            className="px-6 md:px-8"
            style={{ paddingBottom: 24 }}
          >
            <div
              className="flex flex-wrap items-center gap-2"
              style={{ maxWidth: 1180, margin: "0 auto" }}
            >
              <FilterChip
                label="All"
                active={activeCat === null}
                count={all.length}
                onClick={() => setActiveCat(null)}
              />
              {categoriesPresent.map((cat) => (
                <FilterChip
                  key={cat}
                  label={CATEGORY_LABELS[cat] ?? cat}
                  active={activeCat === cat}
                  count={counts[cat] ?? 0}
                  onClick={() => setActiveCat(cat)}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Grid ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(20px, 3vw, 32px)",
            paddingBottom: "clamp(64px, 8vw, 96px)",
          }}
        >
          <div style={{ maxWidth: 1180, margin: "0 auto" }}>
            {vendorsQuery.isLoading ? (
              <div className="flex justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
              </div>
            ) : filtered.length === 0 ? (
              <p
                className="text-center text-sm text-charcoal/65"
                style={{ fontFamily: sansFamily }}
              >
                No vendors in this category yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((v) => (
                  <VendorCard key={v.slug} vendor={v} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── Closing CTA ── */}
        <section
          className="px-6 md:px-8 text-center"
          style={{
            background: "#1D3461",
            color: "#FAF6F0",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(48px, 6vw, 64px)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              aria-hidden="true"
              className="mx-auto mb-7"
              style={{ width: 48, height: 2, background: "#C4564A", borderRadius: 999 }}
            />
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.85rem, 3.4vw, 2.4rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                color: "#FAF6F0",
                margin: "0 auto 14px",
                maxWidth: "22ch",
              }}
            >
              Brief our concierge —{" "}
              <em style={{ fontStyle: "italic", color: "#F7E8A4" }}>
                we'll pick the right one
              </em>
              .
            </h2>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 16,
                lineHeight: 1.55,
                color: "rgba(250,246,240,0.72)",
                margin: "0 auto 26px",
                maxWidth: "44ch",
              }}
            >
              Tell Lembra who's celebrating. We coordinate the vendor, sign the card,
              and book the delivery.
            </p>
            {/* MS67: was a bare "/chat" link that dropped the active
                category filter. If the visitor was scoped to hampers,
                the concierge should open scoped to hampers; otherwise
                they're hunting again from scratch. Mirrors the
                buildChatBridgeUrl pattern used by /browse. */}
            <Link
              to={activeCat ? `/chat?category=${encodeURIComponent(activeCat)}` : "/chat"}
              className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              style={{
                fontFamily: sansFamily,
                boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
              }}
            >
              Open the concierge
              <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: "#1D3461", padding: "0 0 36px" }}>
          <div
            className="mx-auto px-6 md:px-8 border-t border-white/10 pt-10 flex flex-col items-center gap-3"
            style={{ maxWidth: 1180 }}
          >
            <Logo size="sm" onDark />
            <p className="text-xs text-white/50" style={{ fontFamily: sansFamily }}>
              © 2026 Lembra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
